import { Grid } from '@mui/material';
import { Booking } from '@thro-types/leihbar';

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';

import { useBookingMutation, useBookingsQuery } from 'api/hooks/leihbar';
import { ConfirmationDialog, TypedGridColumns } from 'app/components';
import { DataGrid } from 'app/components/DataGrid';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { formatToDateAndTime } from 'utils/dates';
import { useCurrentURL } from 'utils/hooks/useCurrentURL';

import { BookingRowWithTooltip } from '../../components/BookingRowWithTooltip';
import { BookingGridRowActions } from '../../components/rowActionsBookingDataGrid';
import { BookingEditModal } from './components/BookingEditModal';

export const ProductBookingHistory = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const currentURL = useCurrentURL();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [bookingRef, setBookingRef] = useState<Booking | undefined>(undefined);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0
  });

  const { data: bookings, isLoading } = useBookingsQuery({
    params: {
      skip: paginationModel.page * paginationModel.pageSize,
      take: paginationModel.pageSize,
      sort: 'createdAt',
      sortOrder: 'desc',
      productId: id,
      withUserInfo: true
    }
  });

  const { cancel } = useBookingMutation();

  const handlePaginationChange = useCallback(pagModel => {
    setPaginationModel(pagModel);
  }, []);

  const handleBookingCancel = async () => {
    if (!bookingRef) return;
    try {
      await cancel.mutateAsync({
        id: bookingRef.id
      });
      dispatch(
        AlertActions.success(t(translations.pages.leihbar.booking.alerts.successfulCancel()))
      );
    } catch (error) {
      dispatch(AlertActions.error(t(translations.pages.leihbar.booking.alerts.failedCancel())));
    } finally {
      setBookingRef(undefined);
      setCancelModalOpen(false);
    }
  };

  const handleEdit = (booking: Booking) => {
    setBookingRef(booking);
    setEditModalOpen(true);
  };

  const handleStorno = (booking: Booking) => {
    setBookingRef(booking);
    setCancelModalOpen(true);
  };

  const columns: TypedGridColumns<Booking> = [
    {
      field: 'article',
      flex: 1,
      headerName: t(translations.pages.leihbar.article.create.articleId()),
      renderCell: params => params.row.article!.sku,
      sortable: false
    },
    {
      field: 'order',
      flex: 2,
      headerName: t(translations.pages.leihbar.lender()),
      sortable: false,
      renderCell: params => {
        return (
          <a
            href={`mailto:${params.row.order!.email}`}
            onClick={e => e.stopPropagation()}
            className="link"
          >
            {params.row.order!.name}
          </a>
        );
      }
    },
    {
      field: 'startTime',
      flex: 1.5,
      headerName: t(translations.pages.leihbar.booking.columns.start()),
      renderCell: params => formatToDateAndTime(params.value),
      sortable: false
    },
    {
      field: 'endTime',
      flex: 1.5,
      headerName: t(translations.pages.leihbar.booking.columns.end()),
      renderCell: params => formatToDateAndTime(params.value),
      sortable: false
    },
    {
      flex: 1,
      headerName: t(translations.common.actions()),
      field: 'actions',
      type: 'actions',
      getActions: BookingGridRowActions({ handleEdit, handleStorno })
    }
  ];

  return (
    <>
      <BookingEditModal
        closeHandler={() => {
          setBookingRef(undefined);
          setEditModalOpen(false);
        }}
        booking={bookingRef}
        open={editModalOpen}
      />
      <ConfirmationDialog
        open={cancelModalOpen}
        title={t(translations.pages.leihbar.booking.dialogs.deleteConfirm.title())}
        onClose={() => {
          setBookingRef(undefined);
          setCancelModalOpen(false);
        }}
        isPending={cancel.isPending}
        onConfirm={() => {
          handleBookingCancel();
        }}
      >
        <div>{t(translations.pages.leihbar.booking.dialogs.deleteConfirm.message())}</div>
      </ConfirmationDialog>

      <Grid container style={{ height: '80vh' }}>
        <DataGrid
          columns={columns}
          rows={bookings?.data || []}
          style={{ height: '100%', width: '100%' }}
          loading={isLoading}
          paginationMode="server"
          sortingMode="server"
          rowCount={bookings?.count || 0}
          paginationModel={{
            page: paginationModel.page,
            pageSize: paginationModel.pageSize
          }}
          onPaginationModelChange={pagModel => {
            handlePaginationChange(pagModel);
          }}
          disableColumnMenu={true}
          localeText={{ noRowsLabel: t(translations.pages.leihbar.booking.noRows()) }}
          onRowClick={params =>
            navigate(`/leihbar/orders/${params.row.order.id}`, {
              state: { back: currentURL, selectedBooking: params.row.id }
            })
          }
          getRowClassName={params => (params.row.deletedAt !== null ? 'row-canceled' : '')}
          slots={{
            row: BookingRowWithTooltip
          }}
        />
      </Grid>
    </>
  );
};
